import { NavigationStackScreenComponent } from 'react-navigation-stack'
import { ScrollView } from 'react-native'
import { CustomHeader } from '../../components/CustomHeader'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import React, { useMemo } from 'react'
import styled from 'styled-components/native'
import { atlTheme } from '../../atlTheme'
import {
  ATLButton,
  ATLDivider,
  ATLSpacing,
  ATLTextBody,
  ATLTextHead,
  ButtonTypeEnum,
  FontWeightEnum,
  SpacingSizesEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { icons } from '../../constants/searchAssets'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { showMessageSnackbar } from '../../components/snackbar/Snackbar'
import { barcodeMaskApp, barcodeMaskWeb } from '../../utils/masks'
import { formatDate, formatDateToBarcode, ifMoneyInt } from '../../utils/formatters'
import { Clipboard } from '@react-native-community/clipboard/dist/Clipboard'
import { DownloadFile } from '../../utils/downloadFile'
import { platformConfig } from '../../constants/platform'
import { useGetSecondBankSlip } from './utils/useGetSecondBankSlip'
import { useGlobal } from '../../global/useGlobal'
import { MAX_BANK_SLIP } from '../../constants/maxBankSlip'
import { Loading } from '../../components/loading/Loading'
import { getFineAndInterestValue, getNewDateHourZero } from './utils/getFineAndInterestValue'

const TopWhiteCard = styled.View`
  background-color: ${atlTheme.ColorSurface};
  margin-vertical: ${atlTheme.SpacingBase}px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  padding-vertical: ${atlTheme.SpacingAtomic}px;
`

const TitleAndValueContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const GenericWhiteCard = styled.View`
  background-color: ${atlTheme.ColorSurface};
  border-radius: 10px;
  margin-vertical: ${atlTheme.SpacingAtomic}px;
`

export const PayInstallmentScreen: NavigationStackScreenComponent = navigation => {
  // @ts-ignore
  const data = navigation.route.params.installmentInfo
  const barcodeMask = platformConfig.isWeb() ? barcodeMaskWeb : barcodeMaskApp
  const dateBarCode = formatDateToBarcode(data?.digitableNumber)
  const expiredBillet = dateBarCode < getNewDateHourZero()
  const { getDuplicateBankSlip, loadingBankSlip } = useGetSecondBankSlip(Coordinator.goToPayInstallment)
  const [secondBankSlipRegistered] = useGlobal('duplicatesBankSlip')

  const fineAndInterestValue = useMemo(() => {
    const [year, month, day] = data.dueDate.split('-').map(dateComponent => Number(dateComponent))
    return getFineAndInterestValue({
      initialDate: new Date(year, month - 1, day),
      finalDate: dateBarCode,
      currentBalance: data.currentBalance,
      interestPercent: data.interestPercent,
      fineValue: data.fineValue,
    })
  }, [])

  const TitleAndValueComponent = (title: string, value: string, last: boolean, textColor?: string) => {
    return (
      <>
        <TitleAndValueContainer>
          <ATLTextBody
            labelBaseProps={{
              multilineLimit: 1,
              textColor: textColor ? textColor : atlTheme.ColorMediumContrast,
              fontSize: 12,
              fontWeight: FontWeightEnum.medium,
            }}
          >
            {title}
          </ATLTextBody>
          <ATLTextBody
            labelBaseProps={{ multilineLimit: 1, textColor: textColor ? textColor : atlTheme.ColorHighContrast }}
          >
            {value}
          </ATLTextBody>
        </TitleAndValueContainer>
        {!last && <ATLDivider dividerBaseProps={{ color: atlTheme.ColorBackground }} />}
      </>
    )
  }

  const onClickCopyWeb = () => {
    try {
      if (navigation) {
        navigator?.clipboard.writeText(data?.digitableNumber)
        showMessageSnackbar(formatMessage(messages.financePayInstallmentScreen_snackbar_copy))
      }
    } catch (error) {
      showMessageSnackbar(formatMessage(messages.error_snackbar_generic))
    }
  }

  const onClickCopyApp = () => {
    try {
      Clipboard.setString(data?.digitableNumber)
      showMessageSnackbar(formatMessage(messages.financePayInstallmentScreen_snackbar_copy))
    } catch (error) {
      showMessageSnackbar(formatMessage(messages.error_snackbar_generic))
    }
  }

  const onClickDownload = () => {
    DownloadFile(data?.urlReport)
  }

  const onClipUpdateBillet = () => {
    getDuplicateBankSlip(data)
  }

  const onClickCopy = platformConfig.isWeb() ? onClickCopyWeb : onClickCopyApp

  return (
    <ScrollView>
      {loadingBankSlip ? (
        <>
          <ATLSpacing type={SpacingSizesEnum.marginal} />
          <Loading />
        </>
      ) : (
        <>
          <TopWhiteCard>
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_conditionType),
              data?.conditionDescription || '',
              false
            )}
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_dueDate),
              formatDate(data.dueDate),
              false
            )}
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_interestPercent),
              `${data?.interestPercent}%/${formatMessage(messages.financePayInstallmentScreen_month)}`,
              true
            )}
          </TopWhiteCard>
          <GenericWhiteCard>
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_originalValue),
              `${ifMoneyInt(data?.currentBalance)}`,
              false
            )}
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_fineValue),
              `${ifMoneyInt(fineAndInterestValue || 0)}`,
              false
            )}
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_payedValue),
              `${ifMoneyInt(data?.payedValue || 0)}`,
              false
            )}
            {TitleAndValueComponent(
              formatMessage(messages.financePayInstallmentScreen_total),
              `${ifMoneyInt(data?.currentBalance + (fineAndInterestValue || 0))}`,
              true,
              atlTheme.ColorBrand1Default
            )}
          </GenericWhiteCard>
          <GenericWhiteCard>
            {!expiredBillet && (
              <ATLTextBody
                textAlignment={TextAlignmentEnum.center}
                labelBaseProps={{ multilineLimit: 1, textColor: atlTheme.ColorMediumContrast }}
              >
                {formatMessage(messages.financePayInstallmentScreen_barCode)}
              </ATLTextBody>
            )}
            <ATLTextHead labelBaseProps={{ multilineLimit: 5, textColor: atlTheme.ColorHighContrast }}>
              {expiredBillet
                ? formatMessage(messages.financePayInstallmentScreen_generete_billet)
                : barcodeMask(data?.digitableNumber)}
            </ATLTextHead>

            {!expiredBillet && (
              <ATLButton
                icon={icons.copyIcon}
                horizontalMargin={atlTheme.SpacingMarginal}
                buttonBaseProps={{
                  fontWeight: FontWeightEnum.medium,
                  borderRadius: atlTheme.BorderRadiusFloatingButton,
                }}
                title={formatMessage(messages.financePayInstallmentScreen_button_copyCode)}
                onPress={onClickCopy}
                type={ButtonTypeEnum.outlined}
              />
            )}

            <ATLButton
              icon={!expiredBillet && icons.download}
              horizontalMargin={atlTheme.SpacingMarginal}
              buttonBaseProps={{ fontWeight: FontWeightEnum.medium, borderRadius: atlTheme.BorderRadiusFloatingButton }}
              title={formatMessage(
                expiredBillet
                  ? messages.financePayInstallmentScreen_button_genereteBillet
                  : messages.financePayInstallmentScreen_button_download
              )}
              onPress={expiredBillet ? onClipUpdateBillet : onClickDownload}
              type={ButtonTypeEnum.outlined}
            />
          </GenericWhiteCard>
        </>
      )}
    </ScrollView>
  )
}
PayInstallmentScreen.navigationOptions = CustomHeader(
  formatMessage(messages.financePayInstallmentScreen_headerTitle),
  true,
  () => Coordinator.goBack()
)
